// CSpell:ignore Geocodes
import { useQuery } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import {
  baseServiceCenterSchema,
  ServiceCenter,
  ServiceCenterGeocodeResponse,
  serviceCenterGeocodeResponseSchema,
  serviceCenterResponseSchema,
} from "../../types/serviceCenter.type";
import { http } from "../../utils/httpCommon";
import { useApiUrls } from "../useApiUrl";
import { apiUrls } from "../../utils/apiUrls";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import { DaylightDateFormat } from "../../constants/DaylightDateFormat";

const byCode = (a: ServiceCenter, b: ServiceCenter) =>
  a.code.localeCompare(b.code);

const byNameAndOwnership = (serviceCenter: ServiceCenter) =>
  serviceCenter.name && serviceCenter.ownership === "C";

const serviceCentersQueryKey = (includeInactive = false) => [
  "getServiceCenters",
  includeInactive ? "all" : "active",
];

const getServiceCenters = async (
  includeInactive = false,
  getServiceCentersUrl: string
) => {
  const { data } = await http.get<ServiceCenter[]>(getServiceCentersUrl, {
    // TODO: diagnostic with BE Team if we can change these params to camelCase
    // eslint-disable-next-line camelcase
    params: { show_inactive: includeInactive },
  });
  return serviceCenterResponseSchema.parse(data);
};

export const useServiceCenters = (
  type: "All" | "CompanyOwned",
  includeInactive = false
) => {
  const hookUrls = useApiUrls();
  const isEndpointHookEnabled = useIsFeatureFlagEnabled(
    "inbound-3763-endpoint-hook-client"
  );
  const { getServiceCenters: getServiceCentersUrl } = isEndpointHookEnabled
    ? hookUrls
    : apiUrls;
  return useQuery({
    staleTime: Infinity,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: serviceCentersQueryKey(includeInactive),
    queryFn: () => getServiceCenters(includeInactive, getServiceCentersUrl),
    select: (data) =>
      type === "CompanyOwned"
        ? data.filter(byNameAndOwnership).sort(byCode)
        : data.sort(byCode),
  });
};

export const useServiceCenterGeocodes = (sicIds: number[]) => {
  const hookUrls = useApiUrls();
  const isEndpointHookEnabled = useIsFeatureFlagEnabled(
    "inbound-3763-endpoint-hook-client"
  );
  const { getServiceCenterGeocodeByIds } = isEndpointHookEnabled
    ? hookUrls
    : apiUrls;
  return useQuery({
    queryKey: ["getServiceCentersGeocode", sicIds],
    queryFn: async () => {
      const { data, status } = await http.get<ServiceCenterGeocodeResponse[]>(
        getServiceCenterGeocodeByIds(sicIds)
      );
      if (status === 204) return [];
      return serviceCenterGeocodeResponseSchema.parse(data);
    },
  });
};

export const useLoadPlanServiceCenters = (date: Dayjs | null) => {
  const apiUrls = useApiUrls();

  return useQuery({
    enabled: Boolean(date),
    queryKey: ["service-centers", date?.format(DaylightDateFormat.ISO_DATE)],
    queryFn: async () => {
      const { data } = await http.get<ServiceCenterGeocodeResponse[]>(
        apiUrls.getServiceCentersForLoadPlanReport(
          date?.format(DaylightDateFormat.ISO_DATE) ?? ""
        )
      );
      return baseServiceCenterSchema.array().parse(data);
    },
  });
};
